import { inject } from '@angular/core';
import {
  ApolloLink,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client/core';
import { ENV } from '../../env/env.dev';
import { NotificationService } from '../services/notification/notification.service';
import { ErrorLink } from '@apollo/client/link/error';
import { HttpLink } from 'apollo-angular/http';

const uri = ENV.graphqlUrl;

// Track shown errors to prevent duplicates
let lastErrorKey = '';
let lastErrorTime = 0;
const ERROR_THROTTLE_MS = 3000; // Don't show same error within 3 seconds

// Utility to show notifications (with throttling)
const errorMessage = (message: string, notify: NotificationService) => {
  const now = Date.now();
  const errorKey = message;

  // Prevent showing the same error within throttle period
  if (errorKey === lastErrorKey && now - lastErrorTime < ERROR_THROTTLE_MS) {
    return;
  }

  lastErrorKey = errorKey;
  lastErrorTime = now;
  notify.error(message);
};

// Correct errorLink using the new Apollo Client v4 ErrorLink constructor
const errorLink = (notify: NotificationService) => {
  return new ErrorLink((errorResponse: any) => {
    if (errorResponse.graphQLErrors) {
      errorResponse.graphQLErrors.forEach(({ message, path }: any) => {
        if (message.includes('Access is denied')) {
          errorMessage(
            ENV.production
              ? "Access Denied: You Don't have Permission"
              : "Access Denied: You Don't have Permission on path: " + path,
            notify,
          );
        } else {
          // Don't clear loadings here - let each component manage its own loading state
          // in their try/catch/finally blocks to prevent infinite loops
          errorMessage(
            ENV.production
              ? 'Error Occurred: Please reload page or contact the System Administrator'
              : 'GraphQL error: ' + message,
            notify,
          );
        }
      });
    }
    if (errorResponse.networkError) {
      if (!ENV.production) {
        console.error('[Network error]:', errorResponse.networkError);
      } else {
        errorMessage(
          'Network Failure: Please check your internet connection',
          notify,
        );
      }
    }

    // Return void to stop the request chain on errors
    // This prevents retry loops
    return;
  });
};

export const createApollo = () => ({
  link: ApolloLink.from([
    errorLink(inject(NotificationService)),
    inject(HttpLink).create({ uri }),
  ]),
  cache: new InMemoryCache(),
  defaultOptions: {
    query: {
      fetchPolicy: 'network-only' as const,
      errorPolicy: 'all' as const, // Return partial data even on errors
    },
    mutate: {
      errorPolicy: 'all' as const, // Return partial data even on errors
    },
  } as any,
});
